import { useMemo } from 'react';

import { RelevancyCardType } from '../types';

import type { EditorialContent } from '@shared-types/editorial.types';

const skillKinds = [
  'Skills',
  'Moments',
  'Saves',
  'Assists',
  'Goal of the Day',
  'Trendsetters',
  'Teamwork',
  'Well Travelled',
  'Talents',
  'European Adventures',
  'Video Show Clips',
] as const;

/**
 * Custom React hook that determines the appropriate `RelevancyCardType` for a given content item
 * based on its type, kind, section, and additional metadata.
 *
 * @param params - An object containing the following properties:
 * @param params.itemType - The type of the item (e.g., 'article', 'promo', 'video', etc.).
 * @param params.isPollGame - Indicates if the item is a poll game.
 * @param params.kind - The specific kind or category of the item.
 * @param params.section - The section or grouping the item belongs to.
 * @param params.teaser - Additional metadata about the item, such as tags.
 *
 * @returns The corresponding `RelevancyCardType` for the provided item properties, or `null` if no match is found.
 *
 * @remarks
 * The hook uses `useMemo` to optimize performance and avoid unnecessary recalculations.
 * The mapping logic is based on business rules for different item types and their associated properties.
 */
export function useRelevancyCardType({
  itemType,
  isPollGame,
  kind,
  section,
  teaser,
}: {
  itemType: string;
  isPollGame: boolean;
  kind: string;
  section: string;
  teaser: EditorialContent;
}) {
  return useMemo(() => {
    const itemTypeMap = {
      article: () => {
        if (section === '3rd Parties/TOTW') return RelevancyCardType.TEAM_OF_THE_WEEK;
        if (kind === 'Players to Watch') return RelevancyCardType.PLAYERS_TO_WATCH;
        if (kind === 'Quizarena') return RelevancyCardType.QUIZARENA;
        if (kind === 'AudioStream') return RelevancyCardType.LIVE_AUDIO;
        if (kind === 'Podcasts') return RelevancyCardType.PODCAST;
        if (section === 'Editorial/Topscorer' || teaser?.tag?.includes('.CARD.TOPSCORER'))
          return RelevancyCardType.TOP_SCORER;
        if (teaser?.tag?.includes('.CARD.PERFORMANCEZONE')) return RelevancyCardType.PERFORMANCE_ZONE;
        if (kind === 'Info') return RelevancyCardType.MESSAGE_CARD;

        return RelevancyCardType.ARTICLE;
      },
      promo: () => {
        if (section === '3rd Parties/TOTW') return RelevancyCardType.TEAM_OF_THE_WEEK;
        if (kind === 'Players to Watch') return RelevancyCardType.PLAYERS_TO_WATCH;
        if (kind === 'Quizarena') return RelevancyCardType.QUIZARENA;
        if (kind === 'AudioStream') return RelevancyCardType.LIVE_AUDIO;
        if (kind === 'Podcasts') return RelevancyCardType.PODCAST;
        if (section === 'Editorial/Topscorer' || teaser?.tag?.includes('.CARD.TOPSCORER'))
          return RelevancyCardType.TOP_SCORER;
        if (teaser?.tag?.includes('.CARD.PERFORMANCEZONE')) return RelevancyCardType.PERFORMANCE_ZONE;

        return RelevancyCardType.ARTICLE;
      },
      video: () => {
        if (isPollGame) {
          return RelevancyCardType.GAMING;
        }
        if (skillKinds.includes(kind)) return RelevancyCardType.SKILLS_GROUP_EIDOS;
        return RelevancyCardType.VIDEO;
      },
      videostory: () => {
        if (isPollGame) {
          return RelevancyCardType.GAMING;
        }
        if (skillKinds.includes(kind)) return RelevancyCardType.SKILLS_GROUP_EIDOS;
        if (kind === 'Info') return RelevancyCardType.MESSAGE_CARD;
        return RelevancyCardType.VIDEO;
      },
      gallery: () => RelevancyCardType.PHOTO_GALLERY,
      photostory: () => {
        if (isPollGame) return RelevancyCardType.GAMING;
        if (section === 'Editorial/Topscorer' || teaser?.tag?.includes('.CARD.TOPSCORER'))
          return RelevancyCardType.TOP_SCORER;
        if (teaser?.tag?.includes('.CARD.PERFORMANCEZONE')) return RelevancyCardType.PERFORMANCE_ZONE;
        return null;
      },
      document: () => RelevancyCardType.DOCUMENT,
    } as const;
    return itemTypeMap[itemType]?.();
  }, [itemType, isPollGame, kind, section, teaser?.tag]);
}
